import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Users, Wifi, WifiOff, LogOut, RefreshCw, Loader2, MapPin, Languages, Clock, Timer, Mail, Download, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cg_admin_token";

const fmtDuration = (s) => {
  s = Math.round(s || 0);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h) return `${h}h ${m}m`;
  return `${m}m ${sec}s`;
};

// Geographic tile-grid layout [row, col] for a compact US map.
const GRID = {
  AK: [0, 0], ME: [0, 10], VT: [1, 9], NH: [1, 10],
  WA: [2, 0], ID: [2, 1], MT: [2, 2], ND: [2, 3], MN: [2, 4], WI: [2, 6], MI: [2, 7], NY: [2, 8], MA: [2, 9],
  OR: [3, 0], NV: [3, 1], WY: [3, 2], SD: [3, 3], IA: [3, 4], IL: [3, 5], IN: [3, 6], OH: [3, 7], PA: [3, 8], NJ: [3, 9], CT: [3, 10],
  CA: [4, 0], UT: [4, 1], CO: [4, 2], NE: [4, 3], MO: [4, 4], KY: [4, 5], WV: [4, 6], VA: [4, 7], MD: [4, 8], DE: [4, 9], RI: [4, 10],
  AZ: [5, 1], NM: [5, 2], KS: [5, 3], AR: [5, 4], TN: [5, 5], NC: [5, 6], SC: [5, 7], DC: [5, 8],
  OK: [6, 3], LA: [6, 4], MS: [6, 5], AL: [6, 6], GA: [6, 7],
  HI: [7, 0], TX: [7, 3], FL: [7, 8],
};
const ROWS = 8, COLS = 11;

function TileMap({ byState }) {
  const counts = {};
  byState.forEach((s) => { counts[s.code] = s; });
  const max = Math.max(1, ...byState.map((s) => s.count));
  const cells = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) cells.push([r, c]);
  const codeAt = (r, c) => Object.keys(GRID).find((k) => GRID[k][0] === r && GRID[k][1] === c);

  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }} data-testid="admin-usmap">
      {cells.map(([r, c], i) => {
        const code = codeAt(r, c);
        if (!code) return <div key={i} />;
        const info = counts[code];
        const n = info?.count || 0;
        const intensity = n > 0 ? 0.18 + 0.82 * (n / max) : 0;
        const bg = n > 0 ? `rgba(31,111,235,${intensity})` : "#f1f5f9";
        const fg = intensity > 0.55 ? "#fff" : "#0b1b34";
        return (
          <div key={i} title={`${info?.name || code}: ${n} user${n === 1 ? "" : "s"}`}
            data-testid={`state-tile-${code}`}
            className="aspect-square rounded-md flex flex-col items-center justify-center text-[10px] font-semibold transition-transform hover:scale-110 cursor-default"
            style={{ backgroundColor: bg, color: fg }}>
            <span>{code}</span>
            {n > 0 && <span className="text-[9px] font-bold">{n}</span>}
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = { blue: "text-brandblue bg-blue-50", green: "text-covered bg-emerald-50", slate: "text-slate-500 bg-slate-100" };
  return (
    <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
      </div>
      <p className="mt-3 font-display text-4xl font-semibold text-navy">{value}</p>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsR, demoR] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/demo-requests`, { headers }),
      ]);
      setData(statsR.data); setDemos(demoR.data.requests || []); setErr(false);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) onLogout();
      else setErr(true);
    } finally { setLoading(false); }
  }, [token, onLogout]);

  const exportDemosCsv = () => {
    const rows = [["Name", "Company", "Position/Department", "State", "Submitted"],
      ...demos.map((d) => [d.name, d.company, d.position, d.state, d.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `demo-requests-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brandblue" /></div>;
  if (err || !data) return <div className="p-10 text-center text-slate-500">Couldn't load stats. <button onClick={load} className="text-brandblue underline">Retry</button></div>;

  const langNames = { en: "English", es: "Spanish", pt: "Portuguese" };
  const langTotal = Object.values(data.by_language).reduce((a, b) => a + b, 0) || 1;
  const maxDay = Math.max(1, ...data.signups_series.map((d) => d.count));

  return (
    <div className="mx-auto max-w-6xl px-5 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-brandblue" />
          <h1 className="font-display text-2xl font-semibold text-navy">CoverGuide Admin</h1>
          <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-covered" data-testid="admin-live">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-covered opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-covered" /></span>
            Live
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="admin-refresh" className="rounded-full"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button variant="outline" onClick={onLogout} data-testid="admin-logout" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><LogOut className="h-4 w-4 mr-2" />Log out</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Signups" value={data.total_users} tone="blue" />
        <StatCard icon={Wifi} label="Online Now" value={data.online} tone="green" />
        <StatCard icon={WifiOff} label="Offline" value={data.offline} tone="slate" />
        <StatCard icon={Timer} label="Avg Usage" value={fmtDuration(data.avg_usage_seconds)} tone="blue" />
      </div>
      <p className="mt-2 text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> "Online" = active in the last {data.online_window_min} minutes. "Avg Usage" = average active time per user. Auto-refreshes every 15s.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-navy mb-4"><MapPin className="h-5 w-5 text-brandteal" /> Users by State</h2>
          <TileMap byState={data.by_state} />
        </div>
        <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Top States</h3>
          {data.by_state.length ? (
            <div className="space-y-2" data-testid="admin-state-list">
              {data.by_state.slice(0, 12).map((s) => (
                <div key={s.code} className="flex items-center justify-between text-sm">
                  <span className="text-navy">{s.name}</span>
                  <span className="font-semibold text-brandblue">{s.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No location data yet.</p>}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy mb-4">New Signups (last 30 days)</h2>
          <div className="flex items-end gap-1 h-40" data-testid="admin-signups-chart">
            {data.signups_series.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 rounded-t bg-brandblue/80 hover:bg-brandblue transition-colors"
                style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }} />
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-6">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3"><Languages className="h-4 w-4" /> By Language</h3>
          <div className="space-y-3" data-testid="admin-language">
            {Object.entries(data.by_language).map(([code, n]) => (
              <div key={code}>
                <div className="flex justify-between text-sm mb-1"><span className="text-navy">{langNames[code] || code}</span><span className="font-semibold text-navy">{n}</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${(n / langTotal) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-slate-100 card-shadow p-6">
        <h2 className="text-lg font-semibold text-navy mb-4">Recent Signups</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-recent">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2 font-medium">Name</th><th className="py-2 font-medium">Status</th><th className="py-2 font-medium">State</th><th className="py-2 font-medium">Language</th><th className="py-2 font-medium">Usage</th><th className="py-2 font-medium">Last active</th><th className="py-2 font-medium">Joined</th>
            </tr></thead>
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2.5 text-navy font-medium">{r.name || "—"}</td>
                  <td className="py-2.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${r.online ? "text-covered" : "text-slate-400"}`}>
                      <span className={`h-2 w-2 rounded-full ${r.online ? "bg-covered" : "bg-slate-300"}`} />{r.online ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td className="py-2.5 text-slate-500">{r.state || "—"}</td>
                  <td className="py-2.5 text-slate-500">{(langNames[r.language] || r.language || "—")}</td>
                  <td className="py-2.5 text-slate-500">{r.total_active_seconds ? fmtDuration(r.total_active_seconds) : "—"}</td>
                  <td className="py-2.5 text-slate-500">{r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}</td>
                  <td className="py-2.5 text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-slate-100 card-shadow p-6" data-testid="admin-demo-requests">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-navy">
            <Mail className="h-5 w-5 text-brandteal" /> Demo Requests
            <span className="rounded-full bg-brandteal/10 px-2.5 py-0.5 text-sm font-semibold text-brandteal">{demos.length}</span>
          </h2>
          {demos.length > 0 && (
            <Button variant="outline" onClick={exportDemosCsv} data-testid="admin-demo-export" className="rounded-full h-9"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
          )}
        </div>
        {demos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-slate-400">
            <Inbox className="h-8 w-8 mb-2" />
            <p>No demo requests yet. Submissions from the "Request a Demo" form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-demo-table">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 font-medium">Name</th><th className="py-2 font-medium">Company</th><th className="py-2 font-medium">Position / Dept</th><th className="py-2 font-medium">State</th><th className="py-2 font-medium">Submitted</th>
              </tr></thead>
              <tbody>
                {demos.map((d, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`demo-row-${i}`}>
                    <td className="py-2.5 text-navy font-medium">{d.name}</td>
                    <td className="py-2.5 text-slate-500">{d.company}</td>
                    <td className="py-2.5 text-slate-500">{d.position}</td>
                    <td className="py-2.5 text-slate-500">{d.state}</td>
                    <td className="py-2.5 text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPortal() {
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [form, setForm] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setTokenState(null); }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/login`, form);
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setTokenState(r.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  if (token) return <div className="min-h-screen bg-slate-50"><Dashboard token={token} onLogout={logout} /></div>;

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-5">
      <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl bg-white border border-slate-100 card-shadow-lg p-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl brand-gradient text-white mb-4"><ShieldCheck className="h-6 w-6" /></div>
        <h1 className="font-display text-2xl font-semibold text-navy">Admin Portal</h1>
        <p className="text-slate-500 text-sm mt-1 mb-6">Restricted access. Authorized staff only.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-navy">Username</Label>
            <Input data-testid="admin-username" autoComplete="username" value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-navy">Password</Label>
            <Input data-testid="admin-password" type="password" autoComplete="current-password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          {error && <p data-testid="admin-error" className="text-sm text-red-600">{error}</p>}
          <Button type="submit" data-testid="admin-login-btn" disabled={busy || !form.username || !form.password}
            className="w-full rounded-full brand-gradient text-white py-6 font-semibold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </Button>
        </div>
      </form>
    </div>
  );
}
